<template>
  <div class="grid grid-cols-12 gap-4">
    <div class="col-span-6">
      <m-select
        data-test="account-type-select"
        v-model="$v.account.type.$model"
        :options="typeOptions"
      />
    </div>
    <div class="col-span-3 relative">
      <m-input
        data-test="account-identifier-input"
        v-model="$v.account.identifier.$model"
        id="identifer"
      />
      <p
        v-if="!$v.account.identifier.unique"
        class="absolute text-red-500 text-xs"
      >
        Identifier needs to be unique
      </p>
      <p
        v-if="!$v.account.identifier.validChars"
        class="absolute text-red-500 text-xs leading-none"
      >
        Can only contain Letters, Numbers, Spaces, (-), (.) or (/)
      </p>
    </div>
    <div class="col-span-2 flex">
      <div class="relative">
        <m-input-percentage
          id="percent"
          data-test="account-tax-free-input"
          :disabled="account.type === 'Accumulation Account'"
          v-model="$v.account.percent.$model"
        />
      </div>
    </div>
    <div v-if="mode === 'edit'" class="col-span-1 flex items-center">
      <button
        class="flex items-center focus:outline-none"
        data-test="add-account-btn"
        :disabled="$v.$invalid"
        :class="[{ 'cursor-not-allowed': $v.$invalid }]"
        @click="handleEdit"
      >
        <!-- eslint-disable max-len -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6  text-green-700"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
        <!-- eslint-enable max-len -->
      </button>
      <button
        class="col-span-1 focus:outline-none"
        @click="$emit('cancel')"
      >
        <!-- eslint-disable max-len -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 text-red-600"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd"
          />
        </svg>
        <!-- eslint-enable max-len -->
      </button>
    </div>
    <div v-else class="col-span-1 flex items-center">
      <button
        class="flex items-center focus:outline-none"
        data-test="add-account-btn"
        :disabled="$v.$invalid"
        :class="[{ 'cursor-not-allowed': $v.$invalid }]"
        @click="handleAdd"
      >
        <!-- eslint-disable max-len -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6  text-blue-800"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
            clip-rule="evenodd"
          />
        </svg>
        <!-- eslint-enable max-len -->
      </button>
      <button
        class="col-span-1 focus:outline-none"
        @click="handleClear"
      >
        <!-- eslint-disable max-len -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 text-red-600"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd"
          />
        </svg>
        <!-- eslint-enable max-len -->
      </button>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { required, decimal } from 'vuelidate/lib/validators';

export const accountTypeMap = {
  'Accumulation Account': {
    label: 'Accumulation Account',
    type: 'accumulation',
  },
  'Account Based (Retirement Phase) Pension': {
    label: 'Account Based (Retirement Phase) Pension',
    type: 'pension',
  },
  'Transition to Retirement Pension': {
    label: 'Transition to Retirement Pension',
    type: 'pension',
  },
  'Market Linked Pension': {
    label: 'Market Linked Pension',
    type: 'pension',
  },
  'Other Pension': {
    label: 'Other Pension',
    type: 'pension',
  },
};

export default {
  name: 'member-account-add',
  props: {
    identifiers: {
      type: Array,
      default: () => ([]),
    },
    editAccount: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      mode: 'add',
      account: {
        type: '',
        identifier: '',
        percent: 0,
      },
    };
  },
  computed: {
    typeOptions() {
      return _.keys(accountTypeMap);
    },
  },
  methods: {
    handleAdd() {
      const accountType = accountTypeMap[this.account.type];
      this.$emit('add-account', {
        ...accountType,
        identifier: this.account.identifier,
        taxFree: this.account.percent / 100,
        value: 0,
      });
      this.handleClear();
    },
    handleEdit() {
      const accountType = accountTypeMap[this.account.type];
      this.$emit('update-account', {
        ...accountType,
        identifier: this.account.identifier,
        taxFree: this.account.percent / 100,
        value: 0,
      });
      this.handleClear();
    },
    handleClear() {
      this.account.type = '';
      this.account.identifier = '';
      this.account.percent = 0;
    },
  },
  watch: {
    account: {
      handler(value) {
        if (value.type === 'Accumulation Account') {
          this.account.identifier = 'Accumulation';
        } else if (this.account.identifier === 'Accumulation') {
          this.account.identifier = '';
        }
      },
      deep: true,
    },
    editAccount: {
      immediate: true,
      handler(value) {
        if (Object.keys(value).length > 0) {
          this.mode = 'edit';
          this.account.type = value.label;
          this.account.identifier = value.identifier;
          this.account.percent = value.taxFree * 100;
        }
      },
    },
  },
  validations() {
    return {
      account: {
        type: {
          required,
        },
        identifier: {
          required,
          unique: value => (!this.identifiers.includes(value)),
          validChars: (value) => {
            const charsRegex = /^[a-zA-Z0-9/ .-]*$/;
            return charsRegex.test(value);
          },
        },
        percent: {
          decimal,
        },
      },
    };
  },
};
</script>
